"use strict";

/**
 * Instance-scoped Loop subscription widget for your-mockups (multiple orders, same product ID).
 */
(function (global) {
    const APP_ID = "5284869";
    const PLACEHOLDER = "mockup-template";
    const myShopifyDomain = window.Shopify?.shop;
    const baseUrl = `https://cdn.loopwork.co/${myShopifyDomain}`;

    const purchaseOptionPrefix = "loop_purchase_option_";
    const oneTimePurchaseId = "loop-one-time-purchase";

    if (!global.loopMockupShared) {
        global.loopMockupShared = {
            storeJson: null,
            storeJsonPromise: null,
            loopPropsUI: null,
            loopPropsUIPromise: null,
            productCache: {},
        };
    }

    if (!global.loopPropsMockup) {
        global.loopPropsMockup = {};
    }

    if (!global.__loopMockupPT) {
        global.__loopMockupPT = {};
    }

    function getMockupPTPref(instanceId) {
        return global.__loopMockupPT?.[instanceId]?.pref || null;
    }

    function setMockupPTPref(instanceId, pref) {
        global.__loopMockupPT[instanceId] = {
            ...(global.__loopMockupPT[instanceId] || {}),
            pref,
        };
    }

    function setMockupDeliveryPrefs(instanceId, prefs) {
        global.__loopMockupPT[instanceId] = {
            ...(global.__loopMockupPT[instanceId] || {}),
            ...prefs,
        };
    }

    function getMockupDeliveryPrefs(instanceId) {
        return global.__loopMockupPT?.[instanceId] || {};
    }

    function inferMockupPrefFromState(instanceId) {
        const spgId = getProps(instanceId)?.sellingPlanGroupId;
        return spgId && spgId !== oneTimePurchaseId ? "SUBSCRIBE" : "ONETIME";
    }

    function findAllocationByPlanId(variant, sellingPlanId) {
        if (!variant || sellingPlanId == null || sellingPlanId === "") {
            return null;
        }
        return (
            variant.selling_plan_allocations?.find(
                (a) =>
                    Number(a.selling_plan_id) === Number(sellingPlanId)
            ) || null
        );
    }

    function findPlanIdByDeliveryLabel(
        loopContainer,
        instanceId,
        variantId,
        deliveryOptionText
    ) {
        if (!loopContainer || !deliveryOptionText) return null;
        const vKey = normalizeMockupId(variantId);
        const selects = loopContainer.querySelectorAll(
            `#loop-mockup-product-variant-${instanceId}-${vKey} select.loop-selling-plan-selector`
        );
        const needle = deliveryOptionText.trim();
        for (const select of selects) {
            const option = Array.from(select.options).find(
                (o) => o.textContent.trim() === needle
            );
            if (option) return option.value;
        }
        return null;
    }

    function restoreMockupSubscriptionSelection(instanceId) {
        const props = getProps(instanceId);
        const variant = props?.variant;
        const loopContainer = getContainer(instanceId);
        if (!variant?.selling_plan_allocations?.length || !loopContainer) {
            return;
        }

        const saved = getMockupDeliveryPrefs(instanceId);
        let spgId = saved.sellingPlanGroupId || null;
        let planId = saved.sellingPlanId || null;

        let allocation = findAllocationByPlanId(variant, planId);

        if (!allocation && spgId) {
            allocation =
                variant.selling_plan_allocations.find(
                    (a) =>
                        normalizeMockupId(a.selling_plan_group_id) ===
                        normalizeMockupId(spgId)
                ) || null;
            if (allocation && !planId) {
                planId = allocation.selling_plan_id;
            }
        }

        if (!allocation && saved.deliveryOptionText) {
            const matchedPlanId = findPlanIdByDeliveryLabel(
                loopContainer,
                instanceId,
                variant.id,
                saved.deliveryOptionText
            );
            if (matchedPlanId) {
                allocation = findAllocationByPlanId(variant, matchedPlanId);
                if (allocation) {
                    planId = matchedPlanId;
                    spgId = allocation.selling_plan_group_id;
                }
            }
        }

        if (!allocation) {
            allocation = variant.selling_plan_allocations[0];
            spgId = allocation.selling_plan_group_id;
            planId = allocation.selling_plan_id;
        } else {
            spgId = allocation.selling_plan_group_id;
            if (!planId) planId = allocation.selling_plan_id;
        }

        const vKey = normalizeMockupId(variant.id);
        const sKey = normalizeMockupId(spgId);
        const radio = loopContainer.querySelector(
            `#loop-mockup-radio-${instanceId}-${vKey}-${sKey}`
        );
        const select = loopContainer.querySelector(
            `#loop-mockup-select-${instanceId}-${vKey}-${sKey}`
        );

        if (radio) radio.checked = true;
        if (select && planId != null) {
            select.value = String(planId);
        }

        applyLoopState(instanceId, {
            variantId: variant.id,
            sellingPlanGroupId: spgId,
            sellingPlanGroupName: radio?.dataset?.name || "",
            sellingPlanId: planId,
        });

        setMockupDeliveryPrefs(instanceId, {
            sellingPlanId: planId != null ? String(planId) : null,
            sellingPlanGroupId: spgId != null ? String(spgId) : null,
            deliveryOptionText:
                select?.options[select.selectedIndex]?.textContent?.trim() ||
                saved.deliveryOptionText ||
                null,
        });
    }

    function restoreMockupPurchaseType(instanceId, hasPlans) {
        if (!hasPlans) return;

        let pref = getMockupPTPref(instanceId);
        if (!pref) {
            const ui = global.loopMockupShared.loopPropsUI;
            pref = ui?.byDefaultChooseSubscriptionOption
                ? "SUBSCRIBE"
                : "ONETIME";
            setMockupPTPref(instanceId, pref);
        }

        if (pref === "SUBSCRIBE") {
            restoreMockupSubscriptionSelection(instanceId);
        } else {
            defaultSelectOneTime(instanceId);
        }
    }

    function log(message, ...args) {
        console.log(`[Loop Mockup] ${message}`, ...args);
    }

    function getContainer(instanceId) {
        return document.getElementById(
            `loop-mockup-subscription-container-${instanceId}`
        );
    }

    function getSkeleton(instanceId) {
        return document.getElementById(`loop-mockup-skeleton-${instanceId}`);
    }

    function getProductJson(productId) {
        if (global.loopMockupShared.productCache[productId]) {
            return global.loopMockupShared.productCache[productId];
        }
        const el = document.querySelector(
            `.loopProductQuickJson-${productId}`
        );
        if (!el) return null;
        const data = JSON.parse(el.textContent);
        global.loopMockupShared.productCache[productId] = data;
        return data;
    }

    function filterLoopSellingPlanGroups(sellingPlanGroups) {
        if (!Array.isArray(sellingPlanGroups)) return [];
        return sellingPlanGroups.filter((s) => s.app_id === APP_ID);
    }

    function filterLoopVariants(variants, loopSpgSet) {
        return variants.map((v) => ({
            ...v,
            selling_plan_allocations: (v.selling_plan_allocations || []).filter(
                (s) => loopSpgSet.has(s.selling_plan_group_id)
            ),
        }));
    }

    function buildVariantMaps(product) {
        const variantToSellingPlanGroups = {};
        const variantToSellingPlans = {};
        const sellingPlanGroupToSellingPlans = {};

        product.variants.forEach((variant) => {
            variantToSellingPlanGroups[variant.id] = [];
            variantToSellingPlans[variant.id] = [];
            variant.selling_plan_allocations.forEach((allocation) => {
                const spgId = allocation.selling_plan_group_id;
                if (!variantToSellingPlanGroups[variant.id].includes(spgId)) {
                    variantToSellingPlanGroups[variant.id].push(spgId);
                }
                if (
                    !variantToSellingPlans[variant.id].includes(
                        allocation.selling_plan_id
                    )
                ) {
                    variantToSellingPlans[variant.id].push(
                        allocation.selling_plan_id
                    );
                }
                if (!sellingPlanGroupToSellingPlans[spgId]) {
                    sellingPlanGroupToSellingPlans[spgId] = [];
                }
                if (
                    !sellingPlanGroupToSellingPlans[spgId].includes(
                        allocation.selling_plan_id
                    )
                ) {
                    sellingPlanGroupToSellingPlans[spgId].push(
                        allocation.selling_plan_id
                    );
                }
            });
        });

        return {
            variantToSellingPlanGroups,
            variantToSellingPlans,
            sellingPlanGroupToSellingPlans,
        };
    }

    async function fetchStoreJsonOnce() {
        if (global.loopMockupShared.storeJson) {
            return global.loopMockupShared.storeJson;
        }
        if (global.loopMockupShared.storeJsonPromise) {
            return global.loopMockupShared.storeJsonPromise;
        }
        global.loopMockupShared.storeJsonPromise = fetch(
            `${baseUrl}/store.json`
        )
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);
        global.loopMockupShared.storeJson = await global.loopMockupShared
            .storeJsonPromise;
        return global.loopMockupShared.storeJson;
    }

    async function fetchLoopUIPropsOnce() {
        if (global.loopMockupShared.loopPropsUI) {
            return global.loopMockupShared.loopPropsUI;
        }
        if (global.loopMockupShared.loopPropsUIPromise) {
            return global.loopMockupShared.loopPropsUIPromise;
        }
        const endpoint = `https://d217z8zw4dqir.cloudfront.net/${myShopifyDomain}.json`;
        global.loopMockupShared.loopPropsUIPromise = fetch(endpoint)
            .then((r) => (r.ok ? r.json() : {}))
            .catch(() => ({}));
        global.loopMockupShared.loopPropsUI = await global.loopMockupShared
            .loopPropsUIPromise;
        return global.loopMockupShared.loopPropsUI;
    }

    function formatMoney(container, price, removeEach) {
        if (
            window.Shopify?.locale &&
            window.Shopify?.country &&
            window.Shopify?.currency?.active
        ) {
            const decimalValue = price / 100;
            const formatter = new Intl.NumberFormat(
                `${window.Shopify.locale}-${window.Shopify.country}`,
                {
                    style: "currency",
                    currency: window.Shopify.currency.active,
                }
            );
            let formatted = formatter.format(decimalValue);
            if (removeEach) formatted = formatted.replace(/each/gi, "").trim();
            return formatted;
        }

        const moneyFormat =
            container.querySelector(".loop-mockup-price-money-format")
                ?.innerText || "{{amount}}";
        const moneyWithoutCurrency =
            container.querySelector(
                ".loop-mockup-price-money-without-currency-format"
            )?.innerText || "";
        const priceValue = price / 100;
        let formatted;
        if (moneyFormat.includes("0.00")) {
            formatted = moneyFormat.replace("0.00", priceValue.toFixed(2));
        } else if (moneyFormat.includes("0,00")) {
            formatted = moneyFormat.replace(
                "0,00",
                priceValue.toFixed(2).replace(".", ",")
            );
        } else {
            formatted = moneyWithoutCurrency.replace(
                "0.00",
                priceValue.toFixed(2)
            );
        }
        if (removeEach) formatted = formatted.replace(/each/gi, "").trim();
        return formatted.trim();
    }

    function findVariant(product, variantId) {
        return product.variants.find(
            (v) => Number(v.id) === Number(variantId)
        );
    }

    function getProps(instanceId) {
        return global.loopPropsMockup[instanceId];
    }

    function updateSellingPlanInput(instanceId, sellingPlanId) {
        const container = getContainer(instanceId);
        if (!container) return;
        const input = container.querySelector(
            ".loop-mockup-selling-plan-input"
        );
        if (input) input.value = sellingPlanId || "";
    }

    function syncSelectedGroupClass(instanceId, variant, sellingPlanGroupId) {
        const container = getContainer(instanceId);
        if (!container) return;

        container
            .querySelectorAll(".loop-selected-selling-plan-group")
            .forEach((el) =>
                el.classList.remove("loop-selected-selling-plan-group")
            );

        if (sellingPlanGroupId === oneTimePurchaseId) {
            container
                .querySelectorAll(".loop-one-time-purchase-option")
                .forEach((el) =>
                    el.classList.add("loop-selected-selling-plan-group")
                );
            return;
        }

        const groupEl = container.querySelector(
            `#loop-mockup-${instanceId}-${variant.id}-${sellingPlanGroupId}`
        );
        if (groupEl) {
            groupEl.classList.add("loop-selected-selling-plan-group");
        }
    }

    function updateMockupPrices(instanceId) {
        const props = getProps(instanceId);
        if (!props?.variant) return;
        const container = getContainer(instanceId);
        if (!container) return;

        const variant = props.variant;
        const onetimeEl = container.querySelector(".loop-mockup-price-onetime");
        const onetimeOrigEl = container.querySelector(
            ".loop-mockup-price-onetime-original"
        );
        if (onetimeEl) {
            onetimeEl.textContent = formatMoney(container, variant.price, true);
        }
        if (onetimeOrigEl && variant.compare_at_price > variant.price) {
            onetimeOrigEl.textContent = formatMoney(
                container,
                variant.compare_at_price,
                true
            );
            onetimeOrigEl.classList.remove("loop-display-none");
        }

        container.querySelectorAll(".loop-mockup-price").forEach((el) => {
            const vId = el.dataset.variantId;
            const spgId = el.dataset.spgId;
            if (Number(vId) !== Number(variant.id)) return;
            const allocation = variant.selling_plan_allocations?.find(
                (a) => a.selling_plan_group_id === spgId
            );
            if (!allocation) return;
            const price =
                allocation.per_delivery_price || allocation.price;
            el.textContent = formatMoney(container, price, true);
        });
    }

    function applyLoopState(instanceId, options) {
        const props = getProps(instanceId);
        const container = getContainer(instanceId);
        if (!props || !container) return;

        const variantId = options.variantId || props.selectedVariantId;
        const sellingPlanGroupId =
            options.sellingPlanGroupId || props.sellingPlanGroupId;
        const sellingPlanGroupName =
            options.sellingPlanGroupName || props.sellingPlanGroupName;
        const sellingPlanId =
            options.sellingPlanId !== undefined
                ? options.sellingPlanId
                : props.sellingPlanId;

        if (variantId) props.selectedVariantId = variantId;
        if (sellingPlanGroupId) props.sellingPlanGroupId = sellingPlanGroupId;
        if (sellingPlanGroupName) {
            props.sellingPlanGroupName = sellingPlanGroupName;
        }

        const variant = findVariant(props.product, props.selectedVariantId);
        props.variant = variant;

        let sellingPlan = {};
        let selectedSellingPlanId = sellingPlanId || "";

        if (
            sellingPlanGroupId &&
            sellingPlanGroupId !== oneTimePurchaseId &&
            variant
        ) {
            const allocations = variant.selling_plan_allocations.filter(
                (spa) => spa.selling_plan_group_id === sellingPlanGroupId
            );
            if (selectedSellingPlanId) {
                sellingPlan =
                    allocations.find(
                        (a) =>
                            Number(a.selling_plan_id) ===
                            Number(selectedSellingPlanId)
                    ) || allocations[0] || {};
            } else {
                sellingPlan = allocations[0] || {};
            }
            selectedSellingPlanId = sellingPlan.selling_plan_id || "";
        } else if (sellingPlanGroupId === oneTimePurchaseId) {
            selectedSellingPlanId = "";
            sellingPlan = {};
        }

        props.sellingPlanId = selectedSellingPlanId;
        props.sellingPlan = sellingPlan;
        props.sellingPlanAllocation = variant?.selling_plan_allocations?.find(
            (a) => Number(a.selling_plan_id) === Number(selectedSellingPlanId)
        );

        const variantInput = container.querySelector(
            ".loop-mockup-variant-id-input"
        );
        if (variantInput && variant) variantInput.value = variant.id;

        updateSellingPlanInput(instanceId, selectedSellingPlanId);
        syncSelectedGroupClass(instanceId, variant, sellingPlanGroupId);
        updateMockupPrices(instanceId);

        if (variant?.selling_plan_allocations?.length) {
            syncLoopMockupPurchaseTypeUI(instanceId);
        }
    }

    function toggleVariantBlocks(instanceId, selectedVariantId) {
        const container = getContainer(instanceId);
        if (!container) return;
        container.querySelectorAll(".loop-mockup-variant-block").forEach(
            (block) => {
                const vId = block.dataset.variantId;
                block.style.display =
                    Number(vId) === Number(selectedVariantId)
                        ? "block"
                        : "none";
            }
        );
    }

    function showWidget(instanceId) {
        const container = getContainer(instanceId);
        const skeleton = getSkeleton(instanceId);
        if (skeleton) skeleton.style.display = "none";
        if (container) container.classList.remove("loop-display-none");
    }

    function showSkeleton(instanceId) {
        const skeleton = getSkeleton(instanceId);
        if (skeleton) skeleton.style.display = "block";
    }

    function hideSkeleton(instanceId) {
        const skeleton = getSkeleton(instanceId);
        if (skeleton) skeleton.style.display = "none";
    }

    function showFieldset(instanceId) {
        const container = getContainer(instanceId);
        const fieldset = container?.querySelector(
            `#loop-mockup-selling-plan-fieldset-${instanceId}`
        );
        const label = container?.querySelector(".loop-purchase-options-label");
        if (fieldset) fieldset.classList.remove("loop-display-none");
        if (label) label.classList.remove("loop-display-none");
    }

    function hideFieldset(instanceId) {
        const container = getContainer(instanceId);
        const fieldset = container?.querySelector(
            `#loop-mockup-selling-plan-fieldset-${instanceId}`
        );
        const label = container?.querySelector(".loop-purchase-options-label");
        if (fieldset) fieldset.classList.add("loop-display-none");
        if (label) label.classList.add("loop-display-none");
    }

    function defaultSelectOneTime(instanceId) {
        const container = getContainer(instanceId);
        const radio = container?.querySelector(
            `#loop-mockup-one-time-${instanceId}`
        );
        if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
            return;
        }
        defaultSelectFirstSubscription(instanceId);
    }

    function defaultSelectFirstSubscription(instanceId) {
        const props = getProps(instanceId);
        const variant = props?.variant;
        if (!variant?.selling_plan_allocations?.length) return;

        const firstSpg =
            variant.selling_plan_allocations[0].selling_plan_group_id;
        const radio = getContainer(instanceId)?.querySelector(
            `#loop-mockup-radio-${instanceId}-${normalizeMockupId(variant.id)}-${normalizeMockupId(firstSpg)}`
        );
        if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
    }

    function onPurchaseOptionChange(event) {
        const target = event.target;
        const instanceId = target.dataset.loopMockupInstance;
        if (!instanceId) return;

        const sellingPlanGroupId = target.dataset.id;
        if (sellingPlanGroupId === oneTimePurchaseId) {
            setMockupPTPref(instanceId, "ONETIME");
        } else if (sellingPlanGroupId) {
            setMockupPTPref(instanceId, "SUBSCRIBE");
        }
        const sellingPlanGroupName = target.dataset.name;
        const variantId =
            target.dataset.variantId || getProps(instanceId)?.selectedVariantId;

        let sellingPlanId = null;
        if (sellingPlanGroupId !== oneTimePurchaseId && variantId) {
            const select = getContainer(instanceId)?.querySelector(
                `#loop-mockup-select-${instanceId}-${normalizeMockupId(variantId)}-${normalizeMockupId(sellingPlanGroupId)}`
            );
            sellingPlanId = select?.value || null;
        }

        applyLoopState(instanceId, {
            variantId,
            sellingPlanGroupId,
            sellingPlanGroupName,
            sellingPlanId,
        });

        if (sellingPlanGroupId !== oneTimePurchaseId && sellingPlanId) {
            const select = getContainer(instanceId)?.querySelector(
                `#loop-mockup-select-${instanceId}-${normalizeMockupId(variantId)}-${normalizeMockupId(sellingPlanGroupId)}`
            );
            setMockupDeliveryPrefs(instanceId, {
                sellingPlanId: String(sellingPlanId),
                sellingPlanGroupId: String(sellingPlanGroupId),
                deliveryOptionText:
                    select?.options[select.selectedIndex]?.textContent?.trim() ||
                    null,
            });
        }
    }

    function onDeliveryOptionChange(event) {
        const target = event.target;
        const instanceId = target.dataset.loopMockupInstance;
        if (!instanceId) return;
        const props = getProps(instanceId);
        const selectedOption = target.options[target.selectedIndex];

        setMockupDeliveryPrefs(instanceId, {
            sellingPlanId: target.value,
            sellingPlanGroupId: target.dataset.sellingPlanGroupId,
            deliveryOptionText: selectedOption?.textContent?.trim() || null,
        });

        applyLoopState(instanceId, {
            variantId: target.dataset.variantId || props?.selectedVariantId,
            sellingPlanGroupId: target.dataset.sellingPlanGroupId,
            sellingPlanId: target.value,
        });
    }

    function onGroupContainerClick(event) {
        const container =
            event.target.closest(".loop-subscription-group") ||
            event.target.closest(".loop-one-time-purchase-option");
        if (!container) return;

        const radio = container.querySelector('input[type="radio"]');
        if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
    }

    function setupListeners(instanceId) {
        const container = getContainer(instanceId);
        if (!container || container.dataset.loopMockupListeners === "1") {
            return;
        }
        container.dataset.loopMockupListeners = "1";

        container
            .querySelectorAll(`.${"loop-one-time-purchase-option"}`)
            .forEach((el) => {
                el.addEventListener("click", onGroupContainerClick);
            });
        container
            .querySelectorAll(".loop-subscription-group")
            .forEach((el) => {
                el.addEventListener("click", onGroupContainerClick);
            });
        container
            .querySelectorAll(`input[name="${purchaseOptionPrefix}${instanceId}"]`)
            .forEach((el) => {
                el.addEventListener("change", onPurchaseOptionChange);
            });
        container
            .querySelectorAll(".loop-selling-plan-selector")
            .forEach((el) => {
                el.addEventListener("change", onDeliveryOptionChange);
            });
    }

    function normalizeMockupId(id) {
        return id == null ? "" : String(id);
    }

    function findMockupFrequencyContainer(
        loopContainer,
        instanceId,
        variantId,
        spgId
    ) {
        if (!loopContainer || !variantId) return null;

        const vId = normalizeMockupId(variantId);
        const sId = spgId ? normalizeMockupId(spgId) : "";

        if (sId) {
            let el = loopContainer.querySelector(
                `#loop-mockup-selling-plan-container-${instanceId}-${vId}-${sId}`
            );
            if (el) return el;

            el = loopContainer.querySelector(
                `[data-loop-frequency-container][data-loop-mockup-instance="${instanceId}"][data-variant-id="${vId}"][data-spg-id="${sId}"]`
            );
            if (el) return el;
        }

        const variantBlock = loopContainer.querySelector(
            `#loop-mockup-product-variant-${instanceId}-${vId}`
        );
        return (
            variantBlock?.querySelector(
                ".loop-subscription-group-selling-plans-container"
            ) || null
        );
    }

    function showMockupFrequencyContainer(freqContainer, show) {
        if (!freqContainer) return;
        if (show) {
            freqContainer.classList.add("loop-pt-show-frequency");
            freqContainer.style.setProperty("display", "flex", "important");
            freqContainer.style.setProperty("width", "100%", "important");
        } else {
            freqContainer.classList.remove("loop-pt-show-frequency");
            freqContainer.style.removeProperty("display");
            freqContainer.style.removeProperty("width");
        }
    }

    function setMockupFrequencyVisibility(instanceId, show) {
        const loopContainer = getContainer(instanceId);
        if (!loopContainer) return;

        loopContainer
            .querySelectorAll(".loop-selling-plan-allocation")
            .forEach((el) => el.classList.remove("loop-pt-show"));

        loopContainer
            .querySelectorAll("[data-loop-frequency-container]")
            .forEach((el) => showMockupFrequencyContainer(el, false));

        if (!show) return;

        const props = getProps(instanceId);
        const variantId = props?.selectedVariantId;
        const spgId = props?.sellingPlanGroupId;
        if (!variantId || !spgId || spgId === oneTimePurchaseId) return;

        loopContainer
            .querySelectorAll(
                `#loop-mockup-product-variant-${instanceId}-${normalizeMockupId(variantId)} .loop-selling-plan-allocation`
            )
            .forEach((el) => el.classList.add("loop-pt-show"));

        const freqContainer = findMockupFrequencyContainer(
            loopContainer,
            instanceId,
            variantId,
            spgId
        );
        showMockupFrequencyContainer(freqContainer, true);
    }

    function applyMockupDropdownStyling(instanceId) {
        const loopContainer = getContainer(instanceId);
        if (!loopContainer) return;

        loopContainer
            .querySelectorAll("select.loop-selling-plan-selector")
            .forEach((select) => {
                select.className = "";
                select.classList.add("single-option-selector");
            });

        loopContainer
            .querySelectorAll(".loop-selling-plan-selector-label")
            .forEach((lbl) => lbl.classList.add("loop-display-none"));
    }

    function placeMockupSubscribeNote(instanceId) {
        const loopContainer = getContainer(instanceId);
        if (!loopContainer) return;

        const props = getProps(instanceId);
        const variantId = props?.selectedVariantId;
        const spgId = props?.sellingPlanGroupId;
        const isSubActive = spgId && spgId !== oneTimePurchaseId;
        const freqContainer =
            isSubActive && variantId && spgId
                ? findMockupFrequencyContainer(
                      loopContainer,
                      instanceId,
                      variantId,
                      spgId
                  )
                : null;

        let note = loopContainer.querySelector(".loop-pt-note");
        const checkIcon = `
            <span class="loop-pt-note-icon" aria-hidden="true">
                <svg
                    class="loop-pt-note-check-svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M12.228 0.174723C12.6302 0.467223 12.7202 1.02972 12.4277 1.43191L5.22773 11.3319C5.07305 11.5457 4.83398 11.6778 4.56961 11.7003C4.30523 11.7228 4.0493 11.6244 3.86367 11.4388L0.263672 7.83879C-0.0878906 7.48722 -0.0878906 6.91629 0.263672 6.56472C0.615234 6.21316 1.18617 6.21316 1.53773 6.56472L4.39242 9.41941L10.9737 0.371598C11.2662 -0.0305892 11.8287 -0.120589 12.2309 0.171911L12.228 0.174723Z"
                        fill="#3BA25F"
                    />
                </svg>
            </span>
        `;
        if (!note) {
            note = document.createElement("div");
            note.className = "loop-pt-note";
            note.innerHTML = `
                <div class="loop-pt-note-item">
                    ${checkIcon}
                    <span class="loop-pt-note-text">Modify Frequency</span>
                </div>

                <div class="loop-pt-note-item">
                    ${checkIcon}
                    <span class="loop-pt-note-text">Change Photo</span>
                </div>

                <div class="loop-pt-note-item">
                    ${checkIcon}
                    <span class="loop-pt-note-text">Cancel Anytime</span>
                </div>

                <div class="loop-pt-note-item">
                    ${checkIcon}
                    <span class="loop-pt-note-text">Change Scent</span>
                </div>
            `;
        }

        if (!isSubActive || !freqContainer) {
            note.style.display = "none";
            return;
        }

        if (note.parentNode !== freqContainer) {
            freqContainer.appendChild(note);
        }
        note.style.display = "grid";
    }

    function updateMockupCardPrice(instanceId, isSubscribeActive) {
        const loopContainer = getContainer(instanceId);
        const card = loopContainer?.closest(".info-container");
        const priceEl = card?.querySelector(".product_price");
        if (!priceEl) return;

        const props = getProps(instanceId);
        const product = props?.product;
        const variant = props?.variant;
        if (!product || !variant) return;

        let delHtml = "";
        const compareAt = variant.compare_at_price || product.compare_at_price;
        if (compareAt && compareAt > variant.price) {
            delHtml = `<del style="color:#A8A8A8;">${formatMoney(loopContainer, compareAt, true)}</del>`;
        }

        let currentPrice;
        if (isSubscribeActive) {
            const allocation = props.sellingPlanAllocation;
            if (allocation?.per_delivery_price) {
                currentPrice = formatMoney(
                    loopContainer,
                    allocation.per_delivery_price,
                    true
                );
            } else if (allocation?.price) {
                currentPrice = formatMoney(loopContainer, allocation.price, true);
            } else {
                currentPrice = formatMoney(loopContainer, variant.price, true);
            }
        } else {
            currentPrice = formatMoney(loopContainer, variant.price, true);
        }

        priceEl.innerHTML = (delHtml ? delHtml + " " : "") + currentPrice;
    }

    function renderLoopMockupPurchaseTypeButtons(instanceId) {
        const loopContainer = getContainer(instanceId);
        const props = getProps(instanceId);
        if (!loopContainer || !props) return;

        const fieldset = loopContainer.querySelector(
            `#loop-mockup-selling-plan-fieldset-${instanceId}`
        );
        if (!fieldset) return;

        const label = loopContainer.querySelector(".loop-purchase-options-label");
        if (label) {
            label.textContent = "Purchase Type";
            label.style.position = "relative";
            if (!label.querySelector(".loop-mockup-details-link")) {
                const viewDetailsLink = document.createElement("a");
                viewDetailsLink.className =
                    "show_modal subscription-details-link loop-mockup-details-link";
                viewDetailsLink.textContent = "View Details";
                viewDetailsLink.href = "#";
                viewDetailsLink.addEventListener("click", function (e) {
                    e.preventDefault();
                    if (
                        typeof window.openMockupChildSidebar ===
                        "function"
                    ) {
                        window.openMockupChildSidebar(
                            "mySubscription"
                        );
                    } else if (
                        typeof window.openSidebar ===
                        "function"
                    ) {
                        window.openSidebar(
                            "mySubscription"
                        );
                    }
                });
                label.appendChild(viewDetailsLink);
            }
        }

        let ui = loopContainer.querySelector(".loop-pt-buttons");
        if (ui) ui.remove();

        ui = document.createElement("div");
        ui.className = "loop-pt-buttons";
        ui.style.display = "flex";
        ui.style.flexDirection = "column";
        ui.style.gap = "0px";
        fieldset.parentNode.insertBefore(ui, fieldset);

        const product = props.product;
        const variantId = props.selectedVariantId;
        const variant = findVariant(product, variantId);
        if (!variant) return;
  
        const mainPrice = formatMoney(
            loopContainer,
            product.compare_at_price || variant.compare_at_price || variant.price,
            true
        );
        const oneTimePrice = formatMoney(loopContainer, variant.price, true);
        const spgIdPrice = product.selling_plan_groups?.[0]?.id;
        const spAlloc = variant.selling_plan_allocations?.find(
            (a) => a.selling_plan_group_id === spgIdPrice
        );
        const subPrice = spAlloc
            ? formatMoney(
                  loopContainer,
                  spAlloc.per_delivery_price || spAlloc.price,
                  true
              )
            : oneTimePrice;

        const oneRow = document.createElement("label");
        oneRow.className =
            "loop-pt-row loop-pt-btn loop-pt-btn-onetime one-time-label";

        const oneRadioUi = document.createElement("input");
        oneRadioUi.type = "radio";
        oneRadioUi.name = `loop-purchase-type-ui-${instanceId}`;
        oneRadioUi.value = "onetime";
        oneRadioUi.className = "loop-pt-radio";
        oneRadioUi.style.cssText =
            "margin:0px 5px; display:none;";

        const oneLabelText = document.createElement("span");
        oneLabelText.className = "loop-pt-label-text";
        oneLabelText.textContent = "One Time";

        const oneTag = document.createElement("span");
        oneTag.textContent = "Save 25%";
        oneTag.className = "loop-discount-tag";

        const oneTimePriceEl = document.createElement("span");
        oneTimePriceEl.className = "price-container";
        oneTimePriceEl.innerHTML =
            '<del class="main-price-crossed">' +
            mainPrice +
            '</del><span class="main-price-current" style="margin: 0px 15px 0px 5px;">' +
            oneTimePrice +
            "</span>";
        oneTimePriceEl.style.cssText = "margin-left:auto;";

        oneRow.appendChild(oneRadioUi);
        oneRow.appendChild(oneLabelText);
        oneRow.appendChild(oneTag);
        oneRow.appendChild(oneTimePriceEl);

        const subRow = document.createElement("label");
        subRow.className = "loop-pt-row loop-pt-btn loop-pt-btn-subscribe sub-label";

        const inpParent = document.createElement("div");
        inpParent.style.cssText =
            "display:flex;width:100%;align-items:center;gap:0px;cursor:pointer;margin:0;height: inherit;";

        const subRadioUi = document.createElement("input");
        subRadioUi.type = "radio";
        subRadioUi.name = `loop-purchase-type-ui-${instanceId}`;
        subRadioUi.value = "subscribe";
        subRadioUi.className = "loop-pt-radio";
        subRadioUi.style.cssText =
            "margin:0px 5px; display:none;";

        const subLabelText =
        document.createElement("span");

        subLabelText.className =
        "loop-pt-label-text";

        subLabelText.textContent = "Subscribe";

        const subTag = document.createElement("span");
        subTag.textContent = "Save 50%";
        subTag.className = "loop-discount-tag";

        const subPriceEl = document.createElement("span");
        subPriceEl.className = "price-container";
        subPriceEl.innerHTML =
            '<del class="main-price-crossed">' +
            mainPrice +
            '</del><span class="main-price-current" style="margin: 0px 15px 0px 9px;">' +
            subPrice +
            "</span>";
        subPriceEl.style.cssText = "margin-left:auto;";

        inpParent.appendChild(subRadioUi);
        inpParent.appendChild(subLabelText);
        inpParent.appendChild(subTag);
        inpParent.appendChild(subPriceEl);
        subRow.appendChild(inpParent);

        let freqSlot = document.createElement("div");
        freqSlot.className = "loop-mockup-frequency-slot";
        freqSlot.style.width = "100%";
        subRow.appendChild(freqSlot);
 
        ui.appendChild(oneRow);
        ui.appendChild(subRow);

        const initialFreq = findMockupFrequencyContainer(
            loopContainer,
            instanceId,
            variantId,
            product.selling_plan_groups?.[0]?.id
        );
        if (initialFreq) {
            freqSlot.appendChild(initialFreq);
        }

        loopContainer._loopMockupPtUi = {
            oneRow,
            subRow,
            freqSlot,
            oneRadioUi,
            subRadioUi,
        };

        const oneTimeRadio = loopContainer.querySelector(
            `#loop-mockup-one-time-${instanceId}`
        );

        function getCurrentVariantSubRadio() {
            const vId = getProps(instanceId)?.selectedVariantId;
            const activeSpgId = getProps(instanceId)?.sellingPlanGroupId;
            if (!vId) return null;
            const vKey = normalizeMockupId(vId);
            if (activeSpgId && activeSpgId !== oneTimePurchaseId) {
                const sKey = normalizeMockupId(activeSpgId);
                return (
                    loopContainer.querySelector(
                        `#loop-mockup-radio-${instanceId}-${vKey}-${sKey}`
                    ) ||
                    loopContainer.querySelector(
                        `#loop-mockup-product-variant-${instanceId}-${vKey} input.loop-subscription-group-radio`
                    )
                );
            }
            return loopContainer.querySelector(
                `#loop-mockup-product-variant-${instanceId}-${vKey} input.loop-subscription-group-radio`
            );
        }

        function attachFrequencyToSubscribeRow(isSubActive) {
            const slot =
                loopContainer._loopMockupPtUi?.freqSlot ||
                subRow.querySelector(".loop-mockup-frequency-slot");
            if (!slot) return;

            slot
                .querySelectorAll(
                    ".loop-subscription-group-selling-plans-container"
                )
                .forEach((el) => showMockupFrequencyContainer(el, false));

            if (!isSubActive) return;

            const vId = getProps(instanceId)?.selectedVariantId;
            let activeSpgId = getProps(instanceId)?.sellingPlanGroupId;
            if (!vId) return;

            if (!activeSpgId || activeSpgId === oneTimePurchaseId) {
                const variant = findVariant(getProps(instanceId).product, vId);
                activeSpgId =
                    variant?.selling_plan_allocations?.[0]
                        ?.selling_plan_group_id;
            }
            if (!activeSpgId || activeSpgId === oneTimePurchaseId) return;

            const planSelectEl = findMockupFrequencyContainer(
                loopContainer,
                instanceId,
                vId,
                activeSpgId
            );
            if (!planSelectEl) return;

            Array.from(slot.children).forEach((child) => {
                if (child !== planSelectEl) {
                    child.remove();
                }
            });

            if (planSelectEl.parentNode !== slot) {
                slot.appendChild(planSelectEl);
            }
            showMockupFrequencyContainer(planSelectEl, true);
        }

        function syncActiveState() {
            const spgId = getProps(instanceId)?.sellingPlanGroupId;
            const isSubActive = !!(spgId && spgId !== oneTimePurchaseId);

            oneRadioUi.checked = !isSubActive;
            subRadioUi.checked = isSubActive;
            oneRow.classList.toggle("is-active", !isSubActive);
            subRow.classList.toggle("is-active", isSubActive);

            applyMockupDropdownStyling(instanceId);
            attachFrequencyToSubscribeRow(isSubActive);
            setMockupFrequencyVisibility(instanceId, isSubActive);
            placeMockupSubscribeNote(instanceId);
            updateMockupCardPrice(instanceId, isSubActive);
            global.dispatchEvent(
                new CustomEvent(
                    "loop:mockup-purchase-type-change",
                    {
                    detail: {
                        instanceId: instanceId,

                        purchaseType:
                        isSubActive
                            ? "SUBSCRIBE"
                            : "ONETIME",
                    },
                    }
                )
                );

            if (isSubActive) {
                oneRow.style.setProperty("border-bottom", "0", "important");
                subRow.style.removeProperty("border-top");
            } else {
                oneRow.style.removeProperty("border-bottom");
                subRow.style.setProperty("border-top", "0", "important");
            }
        }

        oneRadioUi.addEventListener("change", () => {
            if (!oneRadioUi.checked) return;
            setMockupPTPref(instanceId, "ONETIME");
            if (oneTimeRadio) {
                oneTimeRadio.checked = true;
                oneTimeRadio.dispatchEvent(new Event("change", { bubbles: true }));
            }
            syncActiveState();
        });

        subRadioUi.addEventListener("change", () => {
            if (!subRadioUi.checked) return;
            setMockupPTPref(instanceId, "SUBSCRIBE");
            const ptProps = getProps(instanceId);
            const vId = ptProps?.selectedVariantId;
            const ptVariant = findVariant(ptProps?.product, vId);
            let subRadio = getCurrentVariantSubRadio();

            if (!subRadio && ptVariant?.selling_plan_allocations?.length) {
                const firstSpg =
                    ptVariant.selling_plan_allocations[0].selling_plan_group_id;
                subRadio = loopContainer.querySelector(
                    `#loop-mockup-radio-${instanceId}-${normalizeMockupId(vId)}-${normalizeMockupId(firstSpg)}`
                );
            }

            if (subRadio) {
                subRadio.checked = true;
                subRadio.dispatchEvent(new Event("change", { bubbles: true }));
            }
            syncActiveState();
        });

        loopContainer._loopMockupSyncPtUi = syncActiveState;
        syncActiveState();
    }

    function syncLoopMockupPurchaseTypeUI(instanceId) {
        const loopContainer = getContainer(instanceId);
        if (loopContainer?._loopMockupSyncPtUi) {
            loopContainer._loopMockupSyncPtUi();
            return;
        }
        renderLoopMockupPurchaseTypeButtons(instanceId);
        loopContainer?._loopMockupSyncPtUi?.();
    }

    function initVariantDisplay(instanceId, variantId) {
        const props = getProps(instanceId);
        const variant = findVariant(props.product, variantId);
        if (!variant) return;

        const hasPlans = variant.selling_plan_allocations?.length > 0;
        if (hasPlans) {
            showFieldset(instanceId);
        } else {
            hideFieldset(instanceId);
        }

        toggleVariantBlocks(instanceId, variantId);
        props.selectedVariantId = variantId;
        props.variant = variant;

        restoreMockupPurchaseType(instanceId, hasPlans);

        if (hasPlans) {
            if (!getContainer(instanceId)?.querySelector(".loop-pt-buttons")) {
                renderLoopMockupPurchaseTypeButtons(instanceId);
            } else {
                syncLoopMockupPurchaseTypeUI(instanceId);
            }
        }
    }

    global.rekeyLoopMockupWidget = function (root, instanceId) {
        if (!root || !instanceId || instanceId === PLACEHOLDER) return instanceId;

        const html = root.innerHTML.split(PLACEHOLDER).join(instanceId);
        root.innerHTML = html;

        root.querySelectorAll("[id]").forEach((el) => {
            if (el.id.includes(PLACEHOLDER)) {
                el.id = el.id.split(PLACEHOLDER).join(instanceId);
            }
        });

        root.querySelectorAll("[for]").forEach((el) => {
            const f = el.getAttribute("for");
            if (f && f.includes(PLACEHOLDER)) {
                el.setAttribute("for", f.split(PLACEHOLDER).join(instanceId));
            }
        });

        root.querySelectorAll("[name]").forEach((el) => {
            const n = el.getAttribute("name");
            if (n && n.includes(PLACEHOLDER)) {
                el.setAttribute("name", n.split(PLACEHOLDER).join(instanceId));
            }
        });

        root.querySelectorAll("[data-loop-mockup-instance]").forEach((el) => {
            el.dataset.loopMockupInstance = instanceId;
        });

        if (root.dataset) root.dataset.loopMockupInstance = instanceId;
        return instanceId;
    };

    global.createLoopMockupInstanceId = function (preview) {
        const base =
            preview?.id ||
            preview?.line_item_id ||
            preview?.metadata?.Preview ||
            preview?.metadata?.published_at ||
            "";
        const slug = String(base)
            .replace(/[^a-zA-Z0-9_-]/g, "")
            .slice(0, 40);
        return `mockup-${slug || Math.random().toString(36).slice(2, 11)}`;
    };

    global.initLoopMockupWidget = async function (
        instanceId,
        productId,
        initialVariantId
    ) {
        if (!instanceId || !productId) return;

        showSkeleton(instanceId);

        try {
            const rawProduct = getProductJson(productId);
            if (!rawProduct) {
                log("No product JSON for", productId);
                hideSkeleton(instanceId);
                return;
            }

            const loopSellingPlanGroups = filterLoopSellingPlanGroups(
                rawProduct.selling_plan_groups
            );
            const loopSpgSet = new Set(
                loopSellingPlanGroups.map((g) => g.id)
            );
            const product = {
                ...rawProduct,
                selling_plan_groups: loopSellingPlanGroups,
                variants: filterLoopVariants(rawProduct.variants, loopSpgSet),
            };

            const maps = buildVariantMaps(product);
            const storeJson = await fetchStoreJsonOnce();
            await fetchLoopUIPropsOnce();

            global.loopPropsMockup[instanceId] = {
                instanceId,
                productId,
                product,
                selectedVariantId: initialVariantId,
                storeJson,
                ...maps,
            };

            setupListeners(instanceId);

            let variantId = initialVariantId;
            if (!variantId) {
                const first = product.variants.find((v) => v.available);
                variantId = first?.id;
            }

            initVariantDisplay(instanceId, variantId);
            showWidget(instanceId);
            hideSkeleton(instanceId);
        } catch (err) {
            console.error("[Loop Mockup] init failed", instanceId, err);
            hideSkeleton(instanceId);
        }
    };

    global.setLoopMockupVariant = function (instanceId, variantId) {
        if (!instanceId || !variantId) return;
        if (!getProps(instanceId)) return;
        initVariantDisplay(instanceId, variantId);
    };

    global.getLoopMockupSellingPlanId = function (instanceId) {
        const props = getProps(instanceId);
        if (!props) return "";
        if (props.sellingPlanGroupId === oneTimePurchaseId) return "";
        return props.sellingPlanId || "";
    };

    global.getLoopMockupContainer = getContainer;
})(window);
